import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';
import { CloseIcon } from '../icons';

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Shows a clear button while the field has a value. The button is always
   *  laid out, hidden when empty, so typing never reflows the row. */
  onClear?: () => void;
  /** Classes for the field's frame (width, flex), where `className` styles
   *  the input itself. */
  containerClassName?: string;
}

/**
 * The search field, on the field grammar (fieldStyles.ts): the filled field,
 * square, at the 32px control height so it lines up with the buttons beside
 * it. Its rest stroke is the table register's rule (--table-rule, 1px), so
 * a toolbar search sits in the same register as the table beneath it; the
 * fill and the magnifier identify it as a field, hover darkens the stroke
 * to the field's hover ink, and focus is the accent border with the house
 * ring. The 40px form field keeps the 2px ink-500 field stroke: forms have
 * no table to defer to. Text is 16px below the sm
 * breakpoint, because iOS Safari zooms the page on focus under 16px, and
 * the 13px UI size above it. Literal px for the height, as the app's 13px
 * root shrinks rem.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ disabled, className, containerClassName, onClear, value, ...props }, ref) => (
    <div
      className={cn(
        'flex items-center gap-ds-2 h-[32px] px-3 bg-ds-field border border-table-rule rounded-[var(--radius-field)] transition-colors duration-150',
        !disabled && 'hover:border-ds-border-field-hover',
        'focus-within:border-ds-accent focus-within:ring-[length:var(--focus-ring-width)] focus-within:ring-ds-accent',
        disabled && 'opacity-50',
        containerClassName
      )}
    >
      <svg
        className="shrink-0 text-ds-text-secondary"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="7" cy="7" r="4.5" />
        <line x1="10.5" y1="10.5" x2="14" y2="14" />
      </svg>
      <input
        ref={ref}
        type="search"
        disabled={disabled}
        value={value}
        className={cn(
          'flex-1 border-none bg-transparent font-ds-sans text-[length:var(--type-touch-control)] sm:text-[length:var(--type-ui)] leading-[var(--type-item-lh)] text-ds-text outline-none min-w-0 placeholder:text-ds-text-secondary',
          onClear && '[&::-webkit-search-cancel-button]:appearance-none',
          className
        )}
        {...props}
      />
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear search"
          tabIndex={value ? 0 : -1}
          aria-hidden={!value}
          className={cn(
            'flex items-center justify-center p-[2px] border-none bg-transparent text-ds-text-secondary cursor-pointer rounded-ds-sm transition-colors duration-150 hover:text-ds-text shrink-0',
            !value && 'invisible pointer-events-none'
          )}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  )
);

SearchInput.displayName = 'SearchInput';
